import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Modal,
  Grid,
  Chip,
  Stack,
} from "@mui/material";
import cross from "../../assets/img/cross.png";
import { createRegion, createSubRegion } from "../../api/Api";
import { useTheme } from "@mui/material/styles";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40vw",
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: "none",
};

const AddAreasModal = ({ open, handleClose, onSuccess }) => {
  const theme = useTheme();
  const [region, setRegion] = useState("");
  const [subRegions, setSubRegions] = useState([]);
  const [subRegion, setSubRegion] = useState("");
  const [isLoadingRegion, setIsLoadingRegion] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddRegionButton, setShowAddRegionButton] = useState(true);
  const [isRegionAdded, setIsRegionAdded] = useState(false);
  const [regionId, setRegionId] = useState(null);

  // Function to handle adding a region
  const handleAddRegion = async () => {
    if (region.trim()) {
      setIsLoadingRegion(true); 
      try {
        const response = await createRegion({ region_name: region.trim() });
        setRegionId(response.data.region._id); 
        setShowAddRegionButton(false);
        setIsRegionAdded(true);
      } catch (error) {
        console.error("Error creating region:", error);
      } finally {
        setIsLoadingRegion(false);
      }
    }
  };

  // Function to handle adding a sub-region (Only updates local state)
  const handleAddSubRegion = () => {
    if (subRegion.trim()) {
      setSubRegions((prevSubRegions) => [...prevSubRegions, subRegion.trim()]);
      setSubRegion(""); // Reset the sub-region input field
    }
  };

  // Function to handle deleting a sub-region
  const handleDeleteSubRegion = (subRegionToDelete) => {
    setSubRegions(subRegions.filter((sr) => sr !== subRegionToDelete));
  };
  // Function to handle form submission and save data (API call for Sub-Region happens here)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Save Sub-Regions API
      if (regionId && subRegions.length > 0) {
        const subRegionResponse = await createSubRegion({
          regionId: regionId,
          subRegions: subRegions,
        });

        console.log("Sub-region creation response:", subRegionResponse.data);
      }

      // Execute any other success action if provided
      if (onSuccess) {
        onSuccess({ region: region.trim(), subRegions });
      }
    } catch (error) {
      console.error("Error saving data:", error);
    } finally {
      setIsLoading(false); // Stop loading for save data button
      handleCloseModal();
    }
  };

  // Function to close the modal and reset state
  const handleCloseModal = () => {
    setRegion("");
    setSubRegions([]);
    setSubRegion("");
    setIsLoading(false);
    setIsRegionAdded(false);
    setShowAddRegionButton(true);
    setRegionId(null);
    handleClose();
  };

  // Check if the submit button should be disabled
  const isButtonDisabled = !region.trim() || subRegions.length === 0;

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="add-region-modal-title"
    >
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: "20px",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography id="add-region-modal-title" variant="h6" component="h2">
            Add Delivery Area
          </Typography>
          <Box
            component="img"
            src={cross}
            alt="Close"
            sx={{
              width: "30px",
              height: "30px",
              cursor: "pointer",
            }}
            onClick={handleCloseModal}
          />
        </Box>
        <Box sx={{ padding: "20px" }}>
          <Grid container spacing={2}>
            {/* Region Input Section */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={6} alignItems="center">
                <TextField
                  fullWidth
                  label="Add Region"
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  variant="outlined"
                  required
                  inputProps={{ maxLength: 40 }}
                />
                {showAddRegionButton && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleAddRegion}
                    disabled={isLoadingRegion}
                    sx={{
                      width: "25%",
                      padding: "10px",
                      textTransform: "capitalize",
                    }}
                  >
                    {isLoadingRegion ? "Please wait..." : "Add Region"}
                  </Button>
                )}
              </Stack>
            </Grid>

            {/* Sub-Region Input Section */}
            {isRegionAdded && (
              <>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={6} alignItems="center">
                    <TextField
                      fullWidth
                      label="Add Sub-Region"
                      value={subRegion}
                      onChange={(e) => setSubRegion(e.target.value)}
                      variant="outlined"
                      inputProps={{ maxLength: 40 }}
                    />
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{
                        width: "25%",
                        padding: "10px",
                        textTransform: "capitalize",
                      }}
                      onClick={handleAddSubRegion} 
                    >
                      Add Sub-Region
                    </Button>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", mt: 2 }}>
                    {subRegions.map((subRegion, index) => (
                      <Chip
                        key={index}
                        label={subRegion}
                        onDelete={() => handleDeleteSubRegion(subRegion)}
                        sx={{ margin: 0.5 }}
                      />
                    ))}
                  </Box>
                </Grid>
              </>
            )}

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isButtonDisabled || isLoading}
                  sx={{
                    width: "20%",
                    mt: 2,
                    padding: "15px",
                    backgroundColor: isButtonDisabled
                      ? "#aaa"
                      : isLoading
                      ? "#aaa"
                      : "#FF893A",
                  }}
                >
                  {isLoading ? "Please wait..." : "Save Data"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddAreasModal;
